import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrandSelect } from '@/components/admin/brand-select';
import { AdminButton } from '@/components/admin/button';
import { Card } from '@/components/admin/card';
import { CategoryAttributesCard } from '@/components/admin/category-attributes-card';
import { CategoryPicker } from '@/components/admin/category-picker';
import { AToggle, FormRow } from '@/components/admin/form';
import { AdminIcon } from '@/components/admin/icon';
import { NjangiTiersEditor } from '@/components/admin/njangi-tiers-editor';
import { Markdown } from '@/components/admin/markdown';
import { MarkdownEditor } from '@/components/admin/markdown-editor';
import {
  linksToItems,
  MediaUploader,
  mediaLinksToInputs,
  type UploaderItem,
} from '@/components/admin/media-uploader';
import { AlertBanner } from '@/components/admin/alert-banner';
import { PageHead } from '@/components/admin/page-head';
import {
  ColorSwatch,
  ProductOptionsCard,
  ProductSpecsCard,
} from '@/components/admin/product-options-editor';
import { Tag } from '@/components/admin/tag';
import { AiAssist } from '@/components/ai/ai-assist';
import { ConvertToSimpleModal } from '@/components/admin/convert-to-simple-modal';
import { useConfirm, useToast } from '@/components/ui/feedback';
import { extractErrorMessage } from '@/hooks/use-auth-api';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useAddPackItem,
  useCategoriesList,
  useCreateOffer,
  useCreateProduct,
  useCreateVariant,
  useDeleteOffer,
  useDeleteVariant,
  useHubs,
  usePackPreview,
  useProduct,
  useProductsList,
  useRemovePackItem,
  useReplacePackItems,
  useSetStock,
  useUpdateOffer,
  useUpdatePackItem,
  useUpdateProduct,
  useUpdateVariant,
} from '@/hooks/use-catalog';
import { aiApi, type ProductCopy, type SeoCopy } from '@/lib/ai/ai-api';
import {
  type PackPreview,
  type Product,
  type ProductVariant,
  readProductOptions,
} from '@/lib/catalog/types';
import { fmtFCFA } from '@/lib/format';

type StepKey = 'infos' | 'media' | 'composition' | 'price' | 'variants' | 'seo' | 'review';

interface StepDef {
  k: StepKey;
  l: string;
}

const STEPS: StepDef[] = [
  { k: 'infos', l: 'Informations générales' },
  { k: 'media', l: 'Médias' },
  { k: 'price', l: 'Tarification & stock' },
  { k: 'variants', l: 'Attributs & variantes' },
  { k: 'seo', l: 'SEO & visibilité' },
  { k: 'review', l: 'Validation' },
];

/** P18 — parcours pack : étape Composition avant le prix, pas de variantes. */
const STEPS_PACK: StepDef[] = [
  { k: 'infos', l: 'Informations générales' },
  { k: 'media', l: 'Médias' },
  { k: 'composition', l: 'Composition du pack' },
  { k: 'price', l: 'Tarification' },
  { k: 'seo', l: 'SEO & visibilité' },
  { k: 'review', l: 'Validation' },
];

// ─── Création ──────────────────────────────────────────────────────────────
function CreateForm() {
  const navigate = useNavigate();
  const { data: cats } = useCategoriesList({ isActive: true });
  const createMut = useCreateProduct();
  const [form, setForm] = useState<{
    title: string;
    kind: 'simple' | 'pack';
    categoryId: string;
    brandId: string | null;
    basePrice: number;
    shortDescription: string;
  }>({
    title: '',
    kind: 'simple',
    categoryId: '',
    brandId: null,
    basePrice: 0,
    shortDescription: '',
  });
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!form.categoryId) {
      setErr('Veuillez sélectionner une catégorie.');
      return;
    }
    try {
      const product = await createMut.mutateAsync({
        title: form.title,
        kind: form.kind,
        categoryId: form.categoryId,
        brandId: form.brandId,
        basePrice: form.basePrice,
        shortDescription: form.shortDescription || null,
      });
      navigate(`/catalogue/${product.id}/modifier`);
    } catch (e) {
      setErr(extractErrorMessage(e, 'Création impossible'));
    }
  };

  return (
    <>
      <div className="crumbs" style={{ marginBottom: 14, fontSize: 12 }}>
        <button
          type="button"
          className="root"
          style={{
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
          }}
          onClick={() => navigate('/catalogue')}
        >
          Catalogue
        </button>
        <span className="sep">
          <AdminIcon n="chevR" s={10} />
        </span>
        <span className="here">Nouveau produit</span>
      </div>
      <PageHead
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              onClick={() => navigate('/catalogue')}
            >
              <AdminIcon n="chevL" s={14} />
            </button>
            <span>Nouveau produit</span>
          </span>
        }
        sub="Renseignez l'essentiel, vous pourrez compléter ensuite (médias, variantes, stock, SEO)."
      />
      <Card>
        <form onSubmit={submit} style={{ padding: 18, maxWidth: 720 }}>
          <FormRow
            label="Type de produit"
            helper={
              // Aide contextuelle : n'explique le pack que lorsqu'il est choisi.
              form.kind === 'pack'
                ? 'Un pack regroupe plusieurs articles vendus ensemble sous un seul prix. Le type se fige à la première vente.'
                : undefined
            }
          >
            <div style={{ display: 'flex', gap: 8 }}>
              {(
                [
                  { v: 'simple', l: 'Produit simple' },
                  { v: 'pack', l: 'Pack (vente groupée)' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  className={`chip-filter${form.kind === opt.v ? ' on' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, kind: opt.v }))}
                  data-testid={`product-kind-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </FormRow>
          <FormRow label="Nom du produit" required>
            <input
              className="aform-i"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              maxLength={220}
              placeholder="Ex. Smartphone Tecno Spark 10 — 128 Go"
            />
          </FormRow>
          <FormRow label="Catégorie" required>
            <CategoryPicker
              categories={cats ?? []}
              value={form.categoryId}
              onChange={(id) => setForm((f) => ({ ...f, categoryId: id }))}
              testId="product-category-picker"
            />
          </FormRow>
          <FormRow label="Marque">
            <BrandSelect
              value={form.brandId}
              onChange={(id) => setForm((f) => ({ ...f, brandId: id }))}
              testId="product-brand-select"
            />
          </FormRow>
          <FormRow label="Prix de base (XAF)" required>
            <div className="aform-with-suffix">
              <input
                type="number"
                min={0}
                className="aform-i"
                value={form.basePrice}
                onChange={(e) => setForm((f) => ({ ...f, basePrice: Number(e.target.value) }))}
                required
              />
              <span>F</span>
            </div>
          </FormRow>
          <FormRow
            label="Description courte"
            helper="160 caractères max · résumé pour la liste catalogue"
          >
            <textarea
              className="aform-i"
              rows={3}
              value={form.shortDescription}
              onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))}
              maxLength={500}
            />
          </FormRow>
          {err && <div style={{ color: 'var(--a-danger)', marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <AdminButton variant="ghost" onClick={() => navigate('/catalogue')}>
              Annuler
            </AdminButton>
            <AdminButton variant="primary" type="submit" disabled={createMut.isPending}>
              <AdminIcon n="check" s={14} />
              Créer en brouillon
            </AdminButton>
          </div>
        </form>
      </Card>
    </>
  );
}

// ─── Étape Infos ───────────────────────────────────────────────────────────
function StepInfos({
  product,
  form,
  setForm,
}: {
  product: Product;
  form: EditFormState;
  setForm: React.Dispatch<React.SetStateAction<EditFormState>>;
}) {
  const { data: cats } = useCategoriesList({ isActive: true });
  const [tagDraft, setTagDraft] = useState('');

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    if (!form.tags.includes(t)) setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagDraft('');
  };
  const removeTag = (t: string) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  return (
    <>
      <div className="card">
        <div className="card-h">
          <div>
            <h3>Identité du produit</h3>
            <p className="sub muted">Nom, descriptions — visibles publiquement</p>
          </div>
        </div>
        <div className="card-b">
          <FormRow label="Nom du produit" required helper="Public — affiché sur la fiche produit">
            <input
              className="aform-i"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </FormRow>
          <FormRow
            label="Description courte"
            helper="160 caractères max · résumé pour la liste catalogue"
          >
            <textarea
              className="aform-i"
              rows={2}
              value={form.shortDescription}
              onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))}
            />
          </FormRow>
          <FormRow
            label="Description longue"
            helper="Markdown · onglet Aperçu pour le rendu · 2 000 caractères max"
          >
            <MarkdownEditor
              rows={6}
              maxLength={2000}
              value={form.longDescription}
              onChange={(v) => setForm((f) => ({ ...f, longDescription: v }))}
              testId="product-longdesc-editor"
            />
          </FormRow>
          <AiAssist<ProductCopy>
            label="✨ Rédiger avec l'IA"
            panelTitle="Proposition de fiche produit"
            disabled={!form.title.trim()}
            run={(onText) =>
              aiApi.describeProductStream(
                {
                  title: form.title,
                  brand: form.brand || null,
                  categoryName: cats?.find((c) => c.id === form.categoryId)?.name ?? null,
                  priceXAF: form.basePrice || null,
                  shortDescription: form.shortDescription || null,
                  longDescription: form.longDescription || null,
                },
                onText,
              )
            }
            onApply={(r) =>
              setForm((f) => ({
                ...f,
                title: r.title,
                shortDescription: r.shortDescription,
                longDescription: r.longDescription,
              }))
            }
            renderPreview={(r) => (
              <div style={{ display: 'grid', gap: 8 }}>
                <div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    Titre
                  </div>
                  <strong>{r.title}</strong>
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    Description courte
                  </div>
                  <div>{r.shortDescription}</div>
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    Description longue
                  </div>
                  <div style={{ maxHeight: 220, overflow: 'auto' }}>
                    <Markdown>{r.longDescription}</Markdown>
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <div>
            <h3>Classification</h3>
          </div>
        </div>
        <div className="card-b">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: 16,
            }}
          >
            <FormRow label="Catégorie" required>
              <CategoryPicker
                categories={cats ?? []}
                value={form.categoryId}
                onChange={(id) => setForm((f) => ({ ...f, categoryId: id }))}
                testId="product-category-picker"
              />
            </FormRow>
            <FormRow label="Marque">
              <BrandSelect
                value={form.brandId}
                onChange={(id, b) => setForm((f) => ({ ...f, brandId: id, brand: b?.name ?? '' }))}
                testId="product-brand-select"
              />
            </FormRow>
          </div>
          <FormRow label="Tags / mots-clés" helper="Appuyer sur Entrée pour ajouter">
            <div
              style={{
                display: 'flex',
                gap: 4,
                flexWrap: 'wrap',
                padding: '6px 10px',
                background: 'var(--a-bg)',
                border: '1px solid var(--a-border)',
                borderRadius: 6,
                minHeight: 36,
              }}
            >
              {form.tags.map((t) => (
                <span
                  key={t}
                  className="tag neutral no-dot"
                  style={{
                    paddingRight: 4,
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  #{t}{' '}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    style={{
                      width: 14,
                      height: 14,
                      marginLeft: 4,
                      opacity: 0.6,
                      cursor: 'pointer',
                    }}
                  >
                    <AdminIcon n="close" s={10} />
                  </button>
                </span>
              ))}
              <input
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  flex: 1,
                  minWidth: 80,
                  fontSize: 12,
                }}
                placeholder="Ajouter un tag…"
              />
            </div>
          </FormRow>
          <div className="info-row">
            <span className="l">SKU produit</span>
            <span className="v mono">{product.sku}</span>
          </div>
          <div className="info-row" style={{ borderBottom: 'none' }}>
            <span className="l">Type</span>
            <span className="v">
              {product.kind === 'pack' ? (
                <Tag variant="terra" noDot>
                  Pack
                </Tag>
              ) : (
                'Produit simple'
              )}
              <span className="muted" style={{ marginLeft: 8, fontSize: 11.5 }}>
                figé après la première vente
              </span>
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Étape Médias ──────────────────────────────────────────────────────────
function StepMedia({
  media,
  setMedia,
}: {
  media: UploaderItem[];
  setMedia: React.Dispatch<React.SetStateAction<UploaderItem[]>>;
}) {
  return (
    <div className="card">
      <div className="card-h">
        <div>
          <h3>Médias produit</h3>
          <p className="sub muted">10 photos max · format carré recommandé · 10 Mo par image</p>
        </div>
      </div>
      <div className="card-b">
        <MediaUploader value={media} onChange={setMedia} defaultRole="gallery" />
        <div
          className="muted"
          style={{
            fontSize: 11,
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <AdminIcon n="info" s={12} />
          La première image devient automatiquement la photo principale (affichée dans les
          listings).
        </div>
      </div>
    </div>
  );
}

// ─── Étape Prix & Stock ────────────────────────────────────────────────────
function StepPriceStock({
  product,
  form,
  setForm,
}: {
  product: Product;
  form: EditFormState;
  setForm: React.Dispatch<React.SetStateAction<EditFormState>>;
}) {
  const setStockMut = useSetStock(product.id);
  const updateVariantMut = useUpdateVariant(product.id);
  const { data: hubs = [] } = useHubs();

  const stockFor = (variantId: string, hubId: string): number => {
    const v = product.variants.find((x) => x.id === variantId);
    return v?.stock.find((s) => s.hubId === hubId)?.quantity ?? 0;
  };

  return (
    <>
      <div className="card">
        <div className="card-h">
          <div>
            <h3>Prix</h3>
          </div>
        </div>
        <div className="card-b">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormRow label="Prix de vente" required helper="TTC en FCFA">
              <div className="aform-with-suffix">
                <input
                  className="aform-i"
                  type="number"
                  min={0}
                  value={form.basePrice}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      basePrice: Number(e.target.value),
                    }))
                  }
                />
                <span>F</span>
              </div>
            </FormRow>
            <FormRow label="Prix barré" helper="Pour afficher une remise visuelle">
              <div className="aform-with-suffix">
                <input
                  className="aform-i"
                  type="number"
                  min={0}
                  value={form.compareAtPrice}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      compareAtPrice: Number(e.target.value),
                    }))
                  }
                />
                <span>F</span>
              </div>
            </FormRow>
          </div>
          <div className="divider" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AToggle defaultOn />
            <strong style={{ fontSize: 13 }}>Devise XAF (FCFA)</strong>
            <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>
              TVA gérée par la fiscalité OHADA
            </span>
          </div>
        </div>
      </div>

      {/* P18 : le stock d'un pack est DÉRIVÉ de ses composants — pas de saisie. */}
      {product.kind === 'pack' ? (
        <div className="card">
          <div className="card-h">
            <div>
              <h3>Stock du pack</h3>
              <p className="sub muted">
                Dérivé automatiquement du composant le plus contraint — voir l'étape « Composition
                du pack ».
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-h">
            <div>
              <h3>Stock multi-hub</h3>
              <p className="sub muted">Quantité par variante et par hub logistique</p>
            </div>
          </div>
          <div className="card-b">
            {product.variants.length === 0 || hubs.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                {product.variants.length === 0
                  ? 'Créez au moins une variante (étape suivante) avant de saisir du stock.'
                  : 'Aucun hub configuré — ajoute-en depuis /parametres/hubs.'}
              </p>
            ) : (
              <div className="table-wrap" style={{ margin: 0 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Variante</th>
                      {hubs.map((h) => (
                        <th key={h.id} className="right">
                          {h.code}
                        </th>
                      ))}
                      <th className="right">Seuil bas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.variants.map((v) => (
                      <tr key={v.id}>
                        <td>
                          <strong>{v.name}</strong>
                          <div className="muted mono" style={{ fontSize: 11 }}>
                            {v.sku}
                          </div>
                        </td>
                        {hubs.map((h) => (
                          <td key={h.id} className="right">
                            <input
                              type="number"
                              min={0}
                              defaultValue={stockFor(v.id, h.id)}
                              className="aform-i"
                              style={{ width: 90, textAlign: 'right' }}
                              onBlur={(e) => {
                                const qty = Number(e.target.value);
                                const current = stockFor(v.id, h.id);
                                if (qty !== current) {
                                  setStockMut.mutate({
                                    variantId: v.id,
                                    hubId: h.id,
                                    quantity: qty,
                                  });
                                }
                              }}
                            />
                          </td>
                        ))}
                        <td className="right">
                          <input
                            type="number"
                            min={0}
                            defaultValue={v.stock[0]?.safetyStock ?? 0}
                            className="aform-i"
                            style={{ width: 70, textAlign: 'right' }}
                            onBlur={(e) => {
                              // Met à jour le safetyStock sur le 1er hub existant (suffit MVP).
                              const ss = Number(e.target.value);
                              const first = v.stock[0];
                              if (first && first.safetyStock !== ss) {
                                setStockMut.mutate({
                                  variantId: v.id,
                                  hubId: first.hubId,
                                  quantity: first.quantity,
                                  safetyStock: ss,
                                });
                              }
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-h">
          <div>
            <h3>Dimensions & poids</h3>
            <p className="sub muted">Renseignés sur chaque variante (pour livraison)</p>
          </div>
        </div>
        <div className="card-b">
          <div className="table-wrap" style={{ margin: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Variante</th>
                  <th className="right">Poids (kg)</th>
                  <th className="right">L (cm)</th>
                  <th className="right">l (cm)</th>
                  <th className="right">H (cm)</th>
                </tr>
              </thead>
              <tbody>
                {product.variants.map((v) => (
                  <DimensionsRow key={v.id} variant={v} mutate={updateVariantMut.mutate} />
                ))}
                {product.variants.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 14 }}>
                      Aucune variante.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* P19 — éligibilité njangi + grille de paliers (produits simples : un
          pack ne s'ouvre pas à l'achat groupé au lot 1). */}
      {product.kind !== 'pack' && <NjangiTiersEditor productId={product.id} />}
    </>
  );
}

function DimensionsRow({
  variant,
  mutate,
}: {
  variant: ProductVariant;
  mutate: (args: {
    id: string;
    payload: {
      weightKg?: number | null;
      heightCm?: number | null;
      widthCm?: number | null;
      depthCm?: number | null;
    };
  }) => void;
}) {
  const num = (v: number | null) => (v == null ? '' : v);
  return (
    <tr>
      <td>{variant.name}</td>
      {(['weightKg', 'heightCm', 'widthCm', 'depthCm'] as const).map((key) => (
        <td key={key} className="right">
          <input
            type="number"
            min={0}
            defaultValue={num(variant[key])}
            className="aform-i"
            style={{ width: 80, textAlign: 'right' }}
            onBlur={(e) => {
              const v = e.target.value === '' ? null : Number(e.target.value);
              if (v !== variant[key]) {
                mutate({ id: variant.id, payload: { [key]: v } });
              }
            }}
          />
        </td>
      ))}
    </tr>
  );
}

// ─── Étape Variantes & Offres ─────────────────────────────────────────────
function StepVariants({ product }: { product: Product }) {
  const createMut = useCreateVariant(product.id);
  const updateMut = useUpdateVariant(product.id);
  const deleteMut = useDeleteVariant(product.id);
  const createOfferMut = useCreateOffer(product.id);
  const updateOfferMut = useUpdateOffer(product.id);
  const deleteOfferMut = useDeleteOffer(product.id);
  const { data: hubs = [] } = useHubs();
  const confirm = useConfirm();
  const toast = useToast();
  const [draftName, setDraftName] = useState('');
  const [conversionSimple, setConversionSimple] = useState(false);
  const [conversions, setConversions] = useState(0);
  const { has: aDroit, isSuperAdmin: estSuperAdmin } = usePermissions();
  const peutConvertir = estSuperAdmin || aDroit('CATALOG.DELETE');

  const addVariant = async () => {
    const name = draftName.trim();
    if (!name) return;
    try {
      await createMut.mutateAsync({ name });
      setDraftName('');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Création variante impossible'));
    }
  };

  const deleteVariant = async (v: ProductVariant) => {
    const ok = await confirm({
      title: 'Supprimer la variante',
      message: (
        <>
          Son stock restant sera ramené à zéro et ses offres mises en pause.
          L'action est définitive : tapez le nom de la variante pour confirmer.
        </>
      ),
      requireText: v.name,
      confirmLabel: 'Supprimer',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteMut.mutateAsync({ id: v.id, force: true });
      toast.success(`Variante « ${v.name} » supprimée.`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Suppression impossible'));
    }
  };

  // Axes définis dans attributes.options — pour afficher les chips (et pastilles couleur).
  const optionDefs = readProductOptions(product.attributes);
  const colorOf = (optName: string, label: string): string | undefined =>
    optionDefs
      .find((o) => o.name === optName && o.type === 'color')
      ?.values.find((v) => v.label === label)?.color;

  return (
    <>
      <CategoryAttributesCard product={product} />
      <ProductSpecsCard product={product} />
      {/* Remonté après un passage en produit simple : son état local garderait
          sinon les anciens axes, et un enregistrement les réécrirait. */}
      <ProductOptionsCard key={conversions} product={product} />
      <div className="card">
        <div className="card-h">
          <div>
            <h3>Variantes</h3>
            <p className="sub muted">Chaque variante a son propre SKU, stock et offres</p>
          </div>
          {product.variants.length > 1 && peutConvertir && (
            <AdminButton
              variant="ghost"
              size="sm"
              title="Une seule déclinaison, avec le stock de toutes"
              onClick={() => setConversionSimple(true)}
            >
              Passer en produit simple
            </AdminButton>
          )}
        </div>
        <div className="card-b">
          {product.variants.length === 0 && (
            <div
              style={{
                padding: 12,
                background: 'var(--a-info-soft)',
                borderRadius: 6,
                marginBottom: 10,
                fontSize: 12,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                color: 'var(--a-info)',
              }}
            >
              <AdminIcon n="info" s={14} />
              <span>
                Crée au moins une variante (ex. <em>Standard</em>, <em>Bleu — L</em>) pour pouvoir
                saisir des prix d'offre et du stock.
              </span>
            </div>
          )}
          {product.variants.map((v) => (
            <div
              key={v.id}
              style={{
                padding: '12px 14px',
                border: '1px solid var(--a-border)',
                borderRadius: 6,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                  gap: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <input
                    defaultValue={v.name}
                    className="aform-i"
                    style={{ fontSize: 13, fontWeight: 600 }}
                    onBlur={(e) =>
                      e.target.value !== v.name &&
                      updateMut.mutate({
                        id: v.id,
                        payload: { name: e.target.value },
                      })
                    }
                  />
                  <div
                    className="muted mono"
                    style={{
                      fontSize: 11,
                      marginTop: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    {v.sku}
                    {Object.entries(v.optionValues ?? {}).map(([k, val]) => {
                      const hex = colorOf(k, val);
                      return (
                        <span
                          key={k}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            border: '1px solid var(--a-border)',
                            borderRadius: 999,
                            padding: '1px 8px',
                            fontSize: 10.5,
                            fontFamily: 'var(--a-font)',
                            background: 'var(--a-surface-2)',
                          }}
                        >
                          {hex && <ColorSwatch color={hex} size={10} />}
                          {k} : {val}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={v.isActive}
                    onChange={(e) =>
                      updateMut.mutate({
                        id: v.id,
                        payload: { isActive: e.target.checked },
                      })
                    }
                  />
                  Active
                </label>
                <button
                  type="button"
                  className="top-btn"
                  onClick={() => deleteVariant(v)}
                  style={{ color: 'var(--a-danger)' }}
                >
                  <AdminIcon n="close" s={14} />
                </button>
              </div>

              <FormRow label="Prix override (vide = hérite du prix de base)">
                <div className="aform-with-suffix" style={{ maxWidth: 220 }}>
                  <input
                    type="number"
                    min={0}
                    defaultValue={v.priceOverride ?? ''}
                    className="aform-i"
                    onBlur={(e) => {
                      const val = e.target.value === '' ? null : Number(e.target.value);
                      if (val !== v.priceOverride) {
                        updateMut.mutate({
                          id: v.id,
                          payload: { priceOverride: val },
                        });
                      }
                    }}
                  />
                  <span>F</span>
                </div>
              </FormRow>

              <div className="eyebrow" style={{ margin: '8px 0 6px' }}>
                Offres ({v.offers.length})
              </div>
              {v.offers.length === 0 ? (
                <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
                  Aucune offre — sans offre active, ce produit ne peut pas être publié.
                </p>
              ) : (
                <table className="table" style={{ fontSize: 12, marginBottom: 8 }}>
                  <thead>
                    <tr>
                      <th>Condition</th>
                      <th className="right">Prix</th>
                      <th>Hub préféré</th>
                      <th>Statut</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.offers.map((o) => (
                      <tr key={o.id}>
                        <td>
                          <select
                            defaultValue={o.condition}
                            className="aform-i aform-i-xs"
                            onChange={(e) =>
                              updateOfferMut.mutate({
                                id: o.id,
                                payload: {
                                  condition: e.target.value as typeof o.condition,
                                },
                              })
                            }
                          >
                            <option value="new">Neuf</option>
                            <option value="refurbished">Reconditionné</option>
                            <option value="used">Occasion</option>
                            <option value="open_box">Open box</option>
                          </select>
                        </td>
                        <td className="right">
                          <input
                            type="number"
                            defaultValue={o.price}
                            className="aform-i aform-i-xs"
                            style={{
                              width: 110,
                              textAlign: 'right',
                            }}
                            onBlur={(e) => {
                              const next = Number(e.target.value);
                              if (next !== o.price) {
                                updateOfferMut.mutate({
                                  id: o.id,
                                  payload: { price: next },
                                });
                              }
                            }}
                          />
                        </td>
                        <td>
                          <select
                            defaultValue={o.preferredHubId ?? ''}
                            className="aform-i aform-i-xs"
                            onChange={(e) =>
                              updateOfferMut.mutate({
                                id: o.id,
                                payload: {
                                  preferredHubId: e.target.value || null,
                                },
                              })
                            }
                          >
                            <option value="">—</option>
                            {hubs.map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.code}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            defaultValue={o.status}
                            className="aform-i aform-i-xs"
                            onChange={(e) =>
                              updateOfferMut.mutate({
                                id: o.id,
                                payload: {
                                  status: e.target.value as typeof o.status,
                                },
                              })
                            }
                          >
                            <option value="active">Actif</option>
                            <option value="paused">Pause</option>
                            <option value="expired">Expiré</option>
                          </select>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="top-btn"
                            onClick={() => deleteOfferMut.mutate(o.id)}
                            style={{ color: 'var(--a-danger)' }}
                          >
                            <AdminIcon n="close" s={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <AdminButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  createOfferMut.mutate({
                    variantId: v.id,
                    payload: {
                      price: v.priceOverride ?? product.basePrice,
                      condition: 'new',
                    },
                  })
                }
              >
                <AdminIcon n="plus" s={12} />
                Ajouter une offre
              </AdminButton>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              className="aform-i"
              placeholder="Nom de la nouvelle variante (ex. Bleu — Taille L)"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              style={{ flex: 1 }}
            />
            <AdminButton variant="ghost" onClick={addVariant} disabled={createMut.isPending}>
              <AdminIcon n="plus" s={14} />
              Ajouter variante
            </AdminButton>
          </div>
        </div>
      </div>
      {conversionSimple && (
        <ConvertToSimpleModal
          productId={product.id}
          variants={product.variants}
          defaultVariantId={product.defaultVariantId}
          onClose={() => setConversionSimple(false)}
          onConverted={() => setConversions((n) => n + 1)}
        />
      )}
    </>
  );
}

// ─── Éditeur de tags / mots-clés (réutilisable) ────────────────────────────
function TagsEditor({
  tags,
  onChange,
  placeholder = 'Ajouter un mot-clé…',
}: {
  tags: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setDraft('');
  };
  const remove = (t: string) => onChange(tags.filter((x) => x !== t));
  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        flexWrap: 'wrap',
        padding: '6px 10px',
        background: 'var(--a-bg)',
        border: '1px solid var(--a-border)',
        borderRadius: 6,
        minHeight: 36,
      }}
    >
      {tags.map((t) => (
        <span
          key={t}
          className="tag neutral no-dot"
          style={{
            paddingRight: 4,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          #{t}{' '}
          <button
            type="button"
            onClick={() => remove(t)}
            style={{
              width: 14,
              height: 14,
              marginLeft: 4,
              opacity: 0.6,
              cursor: 'pointer',
            }}
          >
            <AdminIcon n="close" s={10} />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        style={{
          border: 'none',
          background: 'transparent',
          outline: 'none',
          flex: 1,
          minWidth: 80,
          fontSize: 12,
        }}
        placeholder={placeholder}
      />
    </div>
  );
}

// ─── Étape SEO ─────────────────────────────────────────────────────────────
function StepSeo({
  form,
  setForm,
}: {
  form: EditFormState;
  setForm: React.Dispatch<React.SetStateAction<EditFormState>>;
}) {
  const { data: cats } = useCategoriesList({ isActive: true });
  return (
    <div className="card">
      <div className="card-h">
        <div>
          <h3>SEO & visibilité publique</h3>
        </div>
      </div>
      <div className="card-b">
        <FormRow
          label="Slug (URL)"
          required
          helper="Caractères autorisés : a-z, 0-9, tiret. Pas d'accents."
        >
          <div className="aform-with-prefix">
            <span>akwaba.cm/p/</span>
            <input
              className="aform-i"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
            />
          </div>
        </FormRow>
        <FormRow label="Titre meta" required helper="55-60 caractères recommandés">
          <input
            className="aform-i"
            value={form.metaTitle}
            onChange={(e) => setForm((f) => ({ ...f, metaTitle: e.target.value }))}
          />
        </FormRow>
        <FormRow label="Description meta" helper="150-160 caractères recommandés">
          <textarea
            className="aform-i"
            rows={3}
            value={form.metaDescription}
            onChange={(e) => setForm((f) => ({ ...f, metaDescription: e.target.value }))}
          />
        </FormRow>
        <FormRow
          label="Mots-clés (keywords)"
          helper="Entrée pour ajouter · servent à la recherche et sont complétés par l'optimisation SEO ci-dessous"
        >
          <TagsEditor
            tags={form.tags}
            onChange={(t) => setForm((f) => ({ ...f, tags: t }))}
            placeholder="Ajouter un mot-clé SEO…"
          />
        </FormRow>
        <AiAssist<SeoCopy>
          label="✨ Optimiser le SEO avec l'IA"
          panelTitle="Proposition SEO"
          disabled={!form.title.trim()}
          run={(onText) =>
            aiApi.generateSeoStream(
              {
                title: form.title,
                brand: form.brand || null,
                categoryName: cats?.find((c) => c.id === form.categoryId)?.name ?? null,
                shortDescription: form.shortDescription || null,
                longDescription: form.longDescription || null,
                keywords: form.tags.length ? form.tags : null,
              },
              onText,
            )
          }
          onApply={(r) =>
            setForm((f) => ({
              ...f,
              metaTitle: r.metaTitle,
              metaDescription: r.metaDescription,
              tags: Array.from(new Set([...f.tags, ...r.tags])),
            }))
          }
          renderPreview={(r) => (
            <div style={{ display: 'grid', gap: 8 }}>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>
                  Titre meta · {r.metaTitle.length} car.
                </div>
                <strong>{r.metaTitle}</strong>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>
                  Description meta · {r.metaDescription.length} car.
                </div>
                <div>{r.metaDescription}</div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
                  Tags suggérés (fusionnés aux existants)
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {r.tags.map((t) => (
                    <span key={t} className="tag neutral no-dot">
                      #{t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

// ─── Étape Validation ──────────────────────────────────────────────────────
interface ValidationCheck {
  label: string;
  detail: string;
  ok: boolean;
  /** Étape du wizard à ouvrir quand on clique sur la ligne. */
  step: StepKey;
}

function buildValidationChecks(
  product: Product,
  form: EditFormState,
  media: UploaderItem[],
  packPreview: PackPreview | undefined,
): ValidationCheck[] {
  const isPack = product.kind === 'pack';
  const packBlockers = (packPreview?.alerts ?? []).filter(
    (a) => a.code !== 'PACK_NEGATIVE_SAVINGS',
  );
  const variantsOk = product.variants.filter((v) => v.isActive).length;
  const offersOk = product.variants.reduce(
    (a, v) => a + v.offers.filter((o) => o.status === 'active').length,
    0,
  );
  const stockOk = product.variants.reduce(
    (a, v) => a + v.stock.reduce((s, e) => s + e.quantity, 0),
    0,
  );

  return [
    {
      label: 'Informations générales',
      detail: `${form.title || '—'} · ${form.brand || 'Marque non renseignée'}`,
      ok: !!form.title,
      step: 'infos',
    },
    {
      label: 'Médias',
      detail: `${media.length} fichier(s) — ${media.length > 0 ? 'OK' : 'Aucune image'}`,
      ok: media.length > 0,
      step: 'media',
    },
    {
      label: 'Tarification',
      detail: `${fmtFCFA(form.basePrice)} ${
        form.compareAtPrice ? `(prix barré ${fmtFCFA(form.compareAtPrice)})` : ''
      }`,
      ok: form.basePrice > 0,
      step: 'price',
    },
    // P18 : pour un pack, le stock est dérivé et la composition remplace
    // les checks stock/variantes.
    ...(isPack
      ? ([
          {
            label: 'Composition du pack',
            detail:
              (packPreview?.items.length ?? 0) > 0
                ? `${packPreview?.items.length} composant(s) · ${packPreview?.availability ?? 0} pack(s) disponible(s)${packBlockers.length ? ` · ${packBlockers[0].message}` : ''}`
                : 'Aucun composant — un pack vide ne peut pas être publié',
            ok: (packPreview?.items.length ?? 0) > 0 && packBlockers.length === 0,
            step: 'composition',
          },
          {
            label: 'Offres',
            detail: `${offersOk} offre(s) active(s)`,
            ok: offersOk > 0,
            step: 'price',
          },
        ] as ValidationCheck[])
      : ([
          {
            label: 'Stock',
            detail: stockOk > 0 ? `${stockOk} unité(s) cumulées` : 'Stock vide',
            ok: stockOk > 0,
            step: 'price',
          },
          {
            label: 'Variantes',
            detail: `${variantsOk} variante(s) active(s)`,
            ok: variantsOk > 0,
            step: 'variants',
          },
          {
            label: 'Offres',
            detail: `${offersOk} offre(s) active(s)`,
            ok: offersOk > 0,
            step: 'variants',
          },
        ] as ValidationCheck[])),
    {
      label: 'SEO',
      detail: form.slug ? `Slug : ${form.slug}` : 'Slug manquant',
      ok: !!form.slug,
      step: 'seo',
    },
  ];
}

function StepReview({
  checks,
  onSave,
  pending,
  onGoToStep,
}: {
  checks: ValidationCheck[];
  onSave: () => void;
  pending: boolean;
  onGoToStep: (step: StepKey) => void;
}) {
  const allOk = checks.every((c) => c.ok);

  return (
    <div className="card">
      <div className="card-h">
        <div>
          <h3>Validation finale</h3>
          <p className="sub muted">Tous les éléments doivent être verts pour pouvoir publier.</p>
        </div>
      </div>
      <div className="card-b">
        <div
          style={{
            padding: 14,
            background: allOk ? 'var(--a-success-soft)' : 'var(--a-warning-soft)',
            borderRadius: 8,
            display: 'flex',
            gap: 10,
            marginBottom: 16,
            color: allOk ? 'var(--a-success)' : 'var(--a-warning)',
          }}
        >
          <AdminIcon n={allOk ? 'check' : 'alert'} s={20} />
          <div>
            <strong>
              {allOk
                ? 'Toutes les étapes obligatoires sont complètes'
                : 'Quelques éléments manquent pour publier'}
            </strong>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
              Tu peux toujours enregistrer en brouillon pour reprendre plus tard.
            </div>
          </div>
        </div>
        {checks.map((c, i) => (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: checklist item
            key={i}
            type="button"
            onClick={() => onGoToStep(c.step)}
            title={`Aller à l'étape « ${c.label} »`}
            style={{
              display: 'flex',
              gap: 12,
              padding: '10px 0',
              width: '100%',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              borderBottom: i < checks.length - 1 ? '1px solid var(--a-border)' : 'none',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: c.ok ? 'var(--a-success)' : 'var(--a-warning)',
                marginTop: 2,
              }}
            >
              <AdminIcon n={c.ok ? 'check' : 'alert'} s={16} />
            </span>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 13 }}>{c.label}</strong>
              <div className="muted" style={{ fontSize: 12 }}>
                {c.detail}
              </div>
            </div>
            <span className="muted" style={{ marginTop: 2 }}>
              <AdminIcon n="chevR" s={14} />
            </span>
          </button>
        ))}
        <div
          style={{
            marginTop: 20,
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
          }}
        >
          <AdminButton variant="primary" onClick={onSave} disabled={pending}>
            <AdminIcon n="check" s={14} />
            {pending ? 'Sauvegarde…' : allOk ? 'Enregistrer' : 'Enregistrer (brouillon)'}
          </AdminButton>
        </div>
        <p className="muted" style={{ fontSize: 11, marginTop: 10 }}>
          La publication se fait depuis la fiche détail via le bouton « Publier » — elle exige au
          moins 1 image, 1 variante active et 1 offre active.
        </p>
      </div>
    </div>
  );
}

// ─── Wizard édition ────────────────────────────────────────────────────────
// ─── P18 · Étape Composition (pack) ────────────────────────────────────────
function StepComposition({ product }: { product: Product }) {
  const toast = useToast();
  const { data: preview, isLoading } = usePackPreview(product.id);
  const addMut = useAddPackItem(product.id);
  const updateMut = useUpdatePackItem(product.id);
  const removeMut = useRemovePackItem(product.id);
  const replaceMut = useReplacePackItems(product.id);

  // Recherche d'article (composants) — mêmes données que la liste catalogue.
  const [q, setQ] = useState('');
  const searching = q.trim().length >= 2;
  const { data: found } = useProductsList(
    searching ? { search: q.trim(), pageSize: 10 } : { pageSize: 0 },
  );
  const candidates = useMemo(() => {
    if (!searching) return [];
    const inPack = new Set((preview?.items ?? []).map((i) => i.componentVariantId));
    return (found?.items ?? [])
      .filter((p) => p.kind !== 'pack') // I1 — pas de pack dans un pack
      .flatMap((p) =>
        (p.variants ?? []).map((v) => ({
          variantId: v.id,
          label: p.title + (v.name && v.name !== 'Standard' ? ` — ${v.name}` : ''),
          sku: v.sku,
          already: inPack.has(v.id),
        })),
      );
  }, [found, preview, searching]);

  const items = preview?.items ?? [];
  const savingsPct =
    preview?.savings != null && preview.componentsTotal > 0
      ? Math.round((preview.savings / preview.componentsTotal) * 100)
      : null;

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...items];
    const [it] = next.splice(idx, 1);
    next.splice(idx + dir, 0, it);
    try {
      await replaceMut.mutateAsync(
        next.map((i, k) => ({
          componentVariantId: i.componentVariantId,
          quantity: i.quantity,
          sortOrder: k,
          isHighlighted: i.isHighlighted,
        })),
      );
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Réordonnancement impossible'));
    }
  };

  return (
    <>
      {/* Bandeau récapitulatif collant */}
      <div
        className="card"
        style={{ position: 'sticky', top: 76, zIndex: 4 }}
        data-testid="pack-recap"
      >
        <div
          className="card-b"
          style={{
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap',
            alignItems: 'baseline',
          }}
        >
          <div>
            <div className="eyebrow">Valeur des composants</div>
            <strong className="tn">{fmtFCFA(preview?.componentsTotal ?? 0)}</strong>
          </div>
          <div>
            <div className="eyebrow">Prix du pack</div>
            <strong className="tn">
              {preview?.packPrice != null ? fmtFCFA(preview.packPrice) : '—'}
            </strong>
          </div>
          <div>
            <div className="eyebrow">Économie client</div>
            <strong
              className="tn"
              style={{
                color: preview?.savings != null ? 'var(--a-success)' : 'var(--a-ink-mute)',
              }}
            >
              {preview?.savings != null
                ? `${fmtFCFA(preview.savings)}${savingsPct != null ? ` (−${savingsPct} %)` : ''}`
                : '—'}
            </strong>
          </div>
          <div>
            <div className="eyebrow">Disponibilité du pack</div>
            <strong className="tn">{preview ? `${preview.availability} pack(s)` : '—'}</strong>
          </div>
          {preview?.weakestLink && (
            <div>
              <div className="eyebrow">Maillon faible</div>
              <span style={{ fontSize: 12.5 }}>
                {preview.weakestLink.productTitle} · {preview.weakestLink.packsPossible} possible(s)
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Alertes */}
      {(preview?.alerts ?? []).map((a) => (
        <AlertBanner
          key={a.code}
          variant={a.code === 'PACK_NEGATIVE_SAVINGS' ? 'warning' : 'danger'}
          title={a.message}
        />
      ))}

      {/* Recherche d'article */}
      <div className="card">
        <div className="card-h">
          <div>
            <h3>Ajouter un article</h3>
            <div className="sub">
              Recherchez un produit du catalogue — un pack ne peut pas contenir un autre pack.
            </div>
          </div>
        </div>
        <div className="card-b">
          <input
            type="search"
            className="aform-i"
            placeholder="Rechercher un article (nom, SKU)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="pack-component-search"
          />
          {searching && (
            <ul
              style={{
                margin: '8px 0 0',
                padding: 0,
                listStyle: 'none',
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {candidates.length === 0 && (
                <li className="muted" style={{ padding: '8px 4px', fontSize: 12.5 }}>
                  Aucun article trouvé.
                </li>
              )}
              {candidates.map((c) => (
                <li key={c.variantId}>
                  <button
                    type="button"
                    disabled={c.already || addMut.isPending}
                    onClick={async () => {
                      try {
                        await addMut.mutateAsync({
                          componentVariantId: c.variantId,
                        });
                        setQ('');
                      } catch (e) {
                        toast.error(extractErrorMessage(e, 'Ajout impossible'));
                      }
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '8px 10px',
                      borderRadius: 6,
                      background: 'transparent',
                      opacity: c.already ? 0.5 : 1,
                      cursor: c.already ? 'default' : 'pointer',
                    }}
                  >
                    <span>{c.label}</span>
                    <span className="mono muted" style={{ fontSize: 11.5 }}>
                      {c.sku}
                      {c.already ? ' · déjà dans le pack' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Tableau de composition */}
      <div className="card">
        <div className="card-h">
          <div>
            <h3>Composition · {items.length} article(s)</h3>
            <div className="sub">
              Quantités par pack · le stock du pack se déduit du composant le plus contraint.
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="card-b muted">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="empty" style={{ margin: 16 }}>
            Aucun composant — ajoutez au moins un article pour pouvoir publier le pack.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Article</th>
                  <th>SKU</th>
                  <th className="right">Qté / pack</th>
                  <th className="right">PU</th>
                  <th className="right">Sous-total</th>
                  <th className="right">Dispo</th>
                  <th className="right">Packs possibles</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr
                    key={it.id}
                    style={
                      preview?.weakestLink?.componentVariantId === it.componentVariantId
                        ? { background: 'var(--a-surface-2)' }
                        : undefined
                    }
                  >
                    <td>
                      <strong>{it.productTitle}</strong>
                      {it.variantName && it.variantName !== 'Standard' && (
                        <span className="muted"> — {it.variantName}</span>
                      )}
                      {!it.variantActive && (
                        <>
                          {' '}
                          <Tag variant="danger" noDot>
                            Inactive
                          </Tag>
                        </>
                      )}
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {it.variantSku}
                    </td>
                    <td className="right">
                      <input
                        type="number"
                        min={1}
                        max={999}
                        className="aform-i"
                        style={{ width: 72, textAlign: 'right' }}
                        defaultValue={it.quantity}
                        onBlur={async (e) => {
                          const v = Math.max(1, Number(e.target.value) || 1);
                          if (v === it.quantity) return;
                          try {
                            await updateMut.mutateAsync({
                              itemId: it.id,
                              quantity: v,
                            });
                          } catch (err) {
                            toast.error(extractErrorMessage(err, 'Mise à jour impossible'));
                          }
                        }}
                      />
                    </td>
                    <td className="right tn">
                      {it.unitPrice != null ? fmtFCFA(it.unitPrice) : '—'}
                    </td>
                    <td className="right tn">{it.subtotal != null ? fmtFCFA(it.subtotal) : '—'}</td>
                    <td className="right tn">{it.available}</td>
                    <td className="right tn">{it.packsPossible}</td>
                    <td className="right" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        disabled={idx === 0 || replaceMut.isPending}
                        onClick={() => void move(idx, -1)}
                        title="Monter"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        disabled={idx === items.length - 1 || replaceMut.isPending}
                        onClick={() => void move(idx, 1)}
                        title="Descendre"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        disabled={removeMut.isPending}
                        onClick={async () => {
                          try {
                            await removeMut.mutateAsync(it.id);
                          } catch (e) {
                            toast.error(extractErrorMessage(e, 'Retrait impossible'));
                          }
                        }}
                        title="Retirer du pack"
                      >
                        <AdminIcon n="close" s={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

interface EditFormState {
  title: string;
  categoryId: string;
  brand: string;
  brandId: string | null;
  basePrice: number;
  compareAtPrice: number;
  shortDescription: string;
  longDescription: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  tags: string[];
}

function EditWizard({ product }: { product: Product }) {
  const navigate = useNavigate();
  const updateMut = useUpdateProduct(product.id);
  const { data: cats } = useCategoriesList({ isActive: true });

  const [step, setStep] = useState<StepKey>('infos');
  // P18 : parcours dédié pour un pack (étape Composition, pas de variantes).
  const steps = product.kind === 'pack' ? STEPS_PACK : STEPS;
  const [form, setForm] = useState<EditFormState>(() => ({
    title: product.title,
    categoryId: product.categoryId,
    brand: product.brandRef?.name ?? product.brand ?? '',
    brandId: product.brandId,
    basePrice: product.basePrice,
    compareAtPrice: product.compareAtPrice ?? 0,
    shortDescription: product.shortDescription ?? '',
    longDescription: product.longDescription ?? '',
    slug: product.slug,
    metaTitle: product.metaTitle ?? '',
    metaDescription: product.metaDescription ?? '',
    tags: product.tags ?? [],
  }));
  const [media, setMedia] = useState<UploaderItem[]>(() => linksToItems(product.mediaLinks));
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setMedia(linksToItems(product.mediaLinks));
  }, [product.mediaLinks]);

  // Checks de validation partagés entre l'étape Validation, l'en-tête et la
  // barre du bas : l'enregistrement « complet » n'est permis que si tout est
  // vert ; sinon seul l'enregistrement en brouillon reste possible.
  const { data: packPreview } = usePackPreview(product.kind === 'pack' ? product.id : undefined);
  const checks = useMemo(
    () => buildValidationChecks(product, form, media, packPreview),
    [product, form, media, packPreview],
  );
  const allOk = checks.every((c) => c.ok);

  const save = async (): Promise<boolean> => {
    setErr(null);
    try {
      await updateMut.mutateAsync({
        title: form.title,
        categoryId: form.categoryId,
        brandId: form.brandId,
        basePrice: form.basePrice,
        compareAtPrice: form.compareAtPrice || null,
        shortDescription: form.shortDescription || null,
        longDescription: form.longDescription || null,
        slug: form.slug,
        metaTitle: form.metaTitle || null,
        metaDescription: form.metaDescription || null,
        tags: form.tags,
        mediaLinks: mediaLinksToInputs(media),
      });
      return true;
    } catch (e) {
      setErr(extractErrorMessage(e, 'Sauvegarde impossible'));
      return false;
    }
  };

  // Après un enregistrement réussi, retour sur la fiche détail du produit.
  const saveAndClose = async () => {
    if (await save()) navigate(`/catalogue/${product.id}`);
  };

  const statusLabel: Record<Product['status'], string> = {
    draft: 'Brouillon',
    published: 'Publié',
    paused: 'En pause',
    archived: 'Archivé',
  };
  const stepIdx = steps.findIndex((s) => s.k === step);
  const currentCategory = useMemo(
    () => cats?.find((c) => c.id === form.categoryId),
    [cats, form.categoryId],
  );
  const hero =
    media[0]?.url ??
    product.mediaLinks[0]?.url ??
    product.variants[0]?.mediaLinks?.[0]?.url ??
    null;

  return (
    <>
      <div className="crumbs" style={{ marginBottom: 14, fontSize: 12 }}>
        <button
          type="button"
          className="root"
          style={{
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
          }}
          onClick={() => navigate('/catalogue')}
        >
          Catalogue
        </button>
        <span className="sep">
          <AdminIcon n="chevR" s={10} />
        </span>
        <button
          type="button"
          className="root"
          style={{
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
          }}
          onClick={() => navigate(`/catalogue/${product.id}`)}
        >
          {product.sku}
        </button>
        <span className="sep">
          <AdminIcon n="chevR" s={10} />
        </span>
        <span className="here">Modifier</span>
      </div>

      <PageHead
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              onClick={() => navigate(`/catalogue/${product.id}`)}
            >
              <AdminIcon n="chevL" s={14} />
            </button>
            <span>Modifier le produit</span>
            <Tag variant="info" noDot>
              {statusLabel[product.status]}
            </Tag>
          </span>
        }
        sub={
          <>
            SKU <span className="mono">{product.sku}</span> · MAJ{' '}
            {new Date(product.updatedAt).toLocaleString('fr-FR')}
          </>
        }
        actions={
          <>
            <AdminButton variant="ghost" onClick={() => navigate(`/catalogue/${product.id}`)}>
              Annuler
            </AdminButton>
            <AdminButton
              variant="primary"
              onClick={saveAndClose}
              disabled={updateMut.isPending}
              title={
                allOk
                  ? undefined
                  : 'Des éléments de validation manquent — le produit sera enregistré en brouillon'
              }
            >
              <AdminIcon n="check" s={14} />
              {updateMut.isPending
                ? 'Sauvegarde…'
                : allOk
                  ? 'Enregistrer'
                  : 'Enregistrer (brouillon)'}
            </AdminButton>
          </>
        }
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '220px 1fr 320px',
          gap: 20,
          alignItems: 'start',
        }}
      >
        {/* Sidenav steps */}
        <aside className="card" style={{ position: 'sticky', top: 76, padding: 8 }}>
          {steps.map((s, i) => (
            <button
              key={s.k}
              type="button"
              onClick={() => setStep(s.k)}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 6,
                fontSize: 12.5,
                background: step === s.k ? 'var(--a-terra-soft)' : 'transparent',
                color: step === s.k ? 'var(--a-terra-deep)' : 'var(--a-ink-soft)',
                fontWeight: step === s.k ? 600 : 500,
              }}
            >
              <span
                className="tn"
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: step === s.k ? 'var(--a-terracotta)' : 'var(--a-surface-2)',
                  color: step === s.k ? '#fff' : 'var(--a-ink-mute)',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {i + 1}
              </span>
              <span style={{ flex: 1 }}>{s.l}</span>
              {step === s.k && <AdminIcon n="chevR" s={12} />}
            </button>
          ))}
        </aside>

        {/* Main */}
        <div className="stack">
          {step === 'infos' && <StepInfos product={product} form={form} setForm={setForm} />}
          {step === 'media' && <StepMedia media={media} setMedia={setMedia} />}
          {step === 'composition' && <StepComposition product={product} />}
          {step === 'price' && <StepPriceStock product={product} form={form} setForm={setForm} />}
          {step === 'variants' && <StepVariants product={product} />}
          {step === 'seo' && <StepSeo form={form} setForm={setForm} />}
          {step === 'review' && (
            <StepReview
              checks={checks}
              onSave={saveAndClose}
              pending={updateMut.isPending}
              onGoToStep={setStep}
            />
          )}
        </div>

        {/* Preview */}
        <aside style={{ position: 'sticky', top: 76 }}>
          <div className="card">
            <div className="card-h">
              <div>
                <h3>Aperçu fiche produit</h3>
              </div>
            </div>
            <div style={{ padding: 16, background: 'var(--a-bg)' }}>
              <div
                style={{
                  background: '#fff',
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: '1px solid var(--a-border)',
                }}
              >
                <div
                  style={{
                    aspectRatio: '1/1',
                    background: 'var(--a-surface-2)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  {hero ? (
                    <img
                      src={hero}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <AdminIcon n="box" s={32} />
                  )}
                </div>
                <div style={{ padding: 14 }}>
                  <div
                    className="muted"
                    style={{
                      fontSize: 10,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      marginBottom: 4,
                    }}
                  >
                    {currentCategory?.name ?? '—'}
                  </div>
                  <strong
                    style={{
                      display: 'block',
                      fontSize: 14,
                      lineHeight: 1.3,
                      marginBottom: 8,
                    }}
                  >
                    {form.title || 'Sans titre'}
                  </strong>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 6,
                      marginBottom: 6,
                    }}
                  >
                    <strong
                      style={{
                        fontFamily: 'var(--a-font-display)',
                        fontWeight: 900,
                        fontSize: 16,
                        color: 'var(--a-terracotta)',
                      }}
                    >
                      {fmtFCFA(form.basePrice)}
                    </strong>
                    {form.compareAtPrice > 0 && (
                      <span
                        className="muted"
                        style={{ fontSize: 11, textDecoration: 'line-through' }}
                      >
                        {fmtFCFA(form.compareAtPrice)}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--a-ochre)' }}>
                    ☆☆☆☆☆ <span className="muted">(0)</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    style={{ marginTop: 10, width: '100%' }}
                    disabled
                  >
                    Ajouter au panier
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <div className="card-h">
              <div>
                <h3>Tips de création</h3>
              </div>
            </div>
            <div className="card-b" style={{ fontSize: 12 }}>
              <div
                style={{
                  padding: '8px 0',
                  borderBottom: '1px solid var(--a-border)',
                  display: 'flex',
                  gap: 8,
                }}
              >
                <AdminIcon n="info" s={14} c="var(--a-info)" />
                <div>
                  Les <strong>3 premiers mots du titre</strong> doivent contenir l'essentiel — c'est
                  ce qui s'affiche tronqué sur mobile.
                </div>
              </div>
              <div
                style={{
                  padding: '8px 0',
                  borderBottom: '1px solid var(--a-border)',
                  display: 'flex',
                  gap: 8,
                }}
              >
                <AdminIcon n="info" s={14} c="var(--a-info)" />
                <div>
                  Une <strong>photo carrée 1000×1000 px</strong> minimum, fond uni, lumière
                  naturelle.
                </div>
              </div>
              <div style={{ padding: '8px 0', display: 'flex', gap: 8 }}>
                <AdminIcon n="info" s={14} c="var(--a-info)" />
                <div>
                  Pour <strong>publier</strong>, le produit doit avoir au moins 1 image, 1 variante
                  active et 1 offre active.
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {err && <div style={{ color: 'var(--a-danger)', marginTop: 12 }}>{err}</div>}

      {/* Bottom action bar */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          marginTop: 24,
          marginLeft: -24,
          marginRight: -24,
          padding: '14px 24px',
          background: 'var(--a-surface)',
          borderTop: '1px solid var(--a-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 -4px 12px -4px rgba(0,0,0,0.04)',
          zIndex: 5,
        }}
      >
        <div className="muted" style={{ fontSize: 12 }}>
          Étape{' '}
          <strong style={{ color: 'var(--a-ink)' }}>
            {stepIdx + 1} / {steps.length}
          </strong>{' '}
          · {steps[stepIdx]?.l}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <AdminButton
            variant="ghost"
            disabled={stepIdx === 0}
            onClick={() => setStep(steps[stepIdx - 1].k)}
          >
            <AdminIcon n="chevL" s={14} />
            Précédent
          </AdminButton>
          {step !== 'review' ? (
            <AdminButton variant="primary" onClick={() => setStep(steps[stepIdx + 1].k)}>
              Étape suivante
              <AdminIcon n="chevR" s={14} />
            </AdminButton>
          ) : (
            <AdminButton variant="primary" onClick={saveAndClose} disabled={updateMut.isPending}>
              <AdminIcon n="check" s={14} />
              {allOk ? 'Enregistrer' : 'Enregistrer (brouillon)'}
            </AdminButton>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Exports ──────────────────────────────────────────────────────────────
export function AdminProductCreatePage() {
  return <CreateForm />;
}

export function AdminProductEditPage() {
  const { id } = useParams<{ id: string }>();
  const { data: product, isLoading } = useProduct(id);
  if (isLoading || !product) {
    return (
      <div className="card">
        <div className="card-b muted" style={{ textAlign: 'center', padding: 24 }}>
          Chargement du produit…
        </div>
      </div>
    );
  }
  return <EditWizard product={product} />;
}
